// client/src/api/endpoints.ts

// const AUTH_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const AUTH_BASE_URL = (import.meta.env.VITE_API_URL || 'https://api.konectx.net/api').replace(/\/api\/?$/, '');

export const ENDPOINTS = {
  PROFILE: {
    GET_DETAILS: "/user/profile/personal-info",
    UPDATE_DETAILS: "/user/profile/personal-info",
    CHANGE_PASSWORD: "/user/profile/security/change-password",
    DEVICE_LINKED: "/user/profile/devices",
    REVOKE_DEVICE: (ID: string) => `/user/profile/devices/${ID}/revoke`,
    GET_NOTIFICATION: '/user/profile/notification-preferences',
    UPDATE_NOTIFICATION: '/user/profile/notification-preferences',
    ADD_DEVICE: '/user/profile/devices/add',
    UPLOAD_PHOTO: '/user/profile/upload-image',
    TWOAUTH_ENABLE: '/user/profile/security/2fa/enable',
    TWOAUTH_DISABLE: '/user/profile/security/2fa/disable',
    TWOAUTH_STATUS: '/user/profile/security/2fa-status',
    SAVE_PROFILE: '/user/profile/add'
  },

  // ==================== AUTH ====================
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    ME: '/auth/me',
    GOOGLE_LOGIN: `${AUTH_BASE_URL}/api/auth/google`,
    FACEBOOK_LOGIN: `${AUTH_BASE_URL}/api/auth/facebook`,
    VERIFY_EMAIL: '/auth/verify-email',
    RESEND_VERIFICATION: '/auth/resend-verification',
    REFRESH_TOKEN: '/auth/refresh',
    VERIFY_2FA: '/auth/2fa/verify',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    CHANGE_PASSWORD: '/auth/change-password',
    INVITATION_SEND: '/auth/invitation/send',
  },

  // ==================== PLANS ====================
  PLAN: {
    GET_ALL: '/plans',
    AVAILABLE_PLAN: '/plans',
    SUBSCRIBE: (id: string) => `/plans/${id}/subscribe`,
    VERIFY: '/plans/subscribe/verify',
    SUBSCRIBE_IVORYPAY_VERIFY: '/plans/subscribe/ivorypay/verify',
    TOPUP_INIT: '/app/plans/topup/init',
    TOPUP_VERIFY: '/app/plans/topup/verify',
    TOPUP_IVORYPAY_VERIFY: '/app/plans/topup/ivorypay/verify',
  },

  // ==================== SUBSCRIPTIONS ====================
  SUBSCRIPTION: {
    ACTIVE: '/subscriptions/active',
    ACTIVESUBSCRIPTION: '/subscriptions/active',
    MANAGE: '/subscriptions/manage',
    CANCEL: (id: string) => `/subscriptions/${id}/cancel`,
    CANCELSUBSCRIPTION: (id: string) => `/subscriptions/${id}/cancel`,
    AUTO_RENEW: (id: string) => `/subscriptions/${id}/auto-renew`,
    AUTORENEW: (id: string) => `/subscriptions/${id}/auto-renew`,
    UPGRADE: (id: string) => `/subscriptions/${id}/upgrade`,
    CONNECTION_HISTORY: '/subscriptions/connections',
    CONNECTIONHISTORY: (page: string, limit: string) => `/subscriptions/connections?page=${page}&limit=${limit}`,
    DEVICES: '/subscriptions/devices',
  },

  // ==================== USER ====================
  USER: {
    PROFILE: '/user/profile',
    SESSIONS: '/user/sessions',
    UPDATE: '/user/update',
    UPDATE_PROFILE: '/user/profile/update',
    PASSWORD: '/user/change-password',
    DEVICES: '/user/devices',
    REMOVE_DEVICE: (deviceId: string) => `/user/devices/${deviceId}`,
    NOTIFICATIONS: '/user/notifications/preferences',
    UPDATE_NOTIFICATIONS: '/user/notifications/update',
    UPLOAD_AVATAR: '/user/avatar/upload',
    TWO_FA: {
      SETUP: '/user/2fa/setup',
      DISABLE: '/user/2fa/disable',
      VERIFY: '/user/2fa/verify',
      STATUS: '/user/2fa/status',
    },
    COUNTRY: '/user/country',
  },

  // ==================== DASHBOARD ====================
  DASHBOARD: {
    OVERVIEW: '/enduser/dashboard/overview',
    OVERFLOWS: '/enduser/dashboard/overview',
    SUBSCRIPTION: '/enduser/dashboard/subscription',
    NEARBY_HOTSPOTS: '/enduser/dashboard/nearby-hotspots',
    QUICK_CONNECT: '/enduser/dashboard/quick-connect',
  },

  // ==================== HOTSPOT ====================
  HOTSPOT: {
    LIST: '/hotspots',
    CREATE: '/hotspots/create',
    GET: '/enduser/hotspots/',
    DETAILS: (id: string) => `/hotspots/${id}`,
    UPDATE: (id: string) => `/hotspots/${id}`,
    DELETE: (id: string) => `/hotspots/${id}`,
    CONNECT: (id: string) => `/enduser/hotspots/${id}/connect`,
    WITNESS: (id: string) => `/witness/${id}`,
    DISCONNECT: (id: string) => `/enduser/hotspots/${id}/disconnect`,
    MAP_VIEW: '/hotspots/map',
    FILTER: '/hotspots/filter',
  },

  // ==================== DEVICES ====================
  DEVICES: {
    AVAILABLE: '/devices/available',
    CONNECT: '/devices/connect',
    SECURITY: '/devices/security',
    SECURITY_VALIDATE: '/devices/security/validate',
    ADD: '/devices',
    REMOVE: (deviceId: string) => `/devices/${deviceId}`,
    STATUS: (deviceId: string) => `/devices/${deviceId}/status`,
  },

  // ==================== WALLET ====================
  WALLET: {
    BALANCE: '/wallet/balance',
    TRANSACTIONS: '/wallet/transactions',
    TRANSACTION_DETAILS: (id: string) => `/wallet/transactions/${id}`,
    EARNINGS_SUMMARY: '/wallet/earnings/summary',
    EARNINGS_MONTHLY: '/wallet/earnings/monthly',
    CLAIM: '/wallet/claim',
    WITHDRAW: '/wallet/withdraw',
    ADDFUND: '/wallet/add-funds',
    ADD_FUNDS: '/wallet/add-funds',
    ADDWITHDRAW: '/wallet/withdraw',
    ADD_FUNDS_CRYPTO: '/wallet/add-funds/crypto',
    CELO_TOPUP_CRYPTO_INIT: '/wallet/celo/topup/crypto/init',
    CELO_TOPUP_CRYPTO_VERIFY: '/wallet/celo/topup/crypto/verify',
    CONVERT: '/wallet/convert',
    EXCHANGE_RATES: '/wallet/exchange-rates',
    BUY_KXT_BALANCE: '/wallet/buy-kxt/balance',
    BUY_KXT_CRYPTO: '/wallet/buy-kxt/crypto',
  },

  // ==================== TOKEN ====================
  TOKEN: {
    SUMMARY: '/token/summary',
    CLAIM: '/token/claim',
    WITHDRAW: '/token/withdraw',
    TRANSACTIONS: '/token/transactions',
    WITHDRAWAL_STATUS: (withdrawalId: string) => `/token/withdraw/${withdrawalId}`,
    DEV_SEED: '/token/dev/seed-balance',
  },

  // ==================== OPERATOR KXT ====================
  OPERATOR_KXT: {
    WALLET:           '/operator/kxt/wallet',
    WALLET_GENERATE:  '/operator/kxt/wallet/generate',
    BALANCE:          '/operator/kxt/balance',
    REWARD_SCORE:     '/operator/kxt/reward-score',
    STREAK:           '/operator/kxt/streak',
    LEADERBOARD:      '/operator/kxt/leaderboard',
    EARNINGS_HISTORY: '/operator/kxt/earnings/history',
    TRANSACTIONS:     '/operator/kxt/transactions',
    CONVERT_USD:      '/operator/kxt/convert/usd',
    CONVERT_KXT:      '/operator/kxt/convert/kxt',
    WITHDRAW:         '/operator/kxt/withdraw',
    WITHDRAWALS:      '/operator/kxt/withdrawals',
    RATE_USD:         '/operator/kxt/rate/usd',
  },

  // ==================== PAYMENT METHODS ====================
  PAYMENT: {
    GET_METHODS: '/payment-methods',
    ADD_METHOD: '/payment-methods',
    DELETE_METHOD: (id: string) => `/payment-methods/${id}`,
    SET_DEFAULT: (id: string) => `/payment-methods/${id}/set-default`,
  },

  // ==================== FLUTTERWAVE ====================
  FLUTTERWAVE: {
    INITIALIZE: '/flutterwave/initialize',
    VERIFY: '/flutterwave/verify',
  },

  // ==================== NOTIFICATIONS ====================
  NOTIFICATIONS: {
    GET_ALL: '/notifications',
    MARK_READ: (id: string) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/mark-all-read',
    DELETE: (id: string) => `/notifications/${id}`,
  },

  // ==================== ANALYTICS ====================
  ANALYTICS: {
    DATA_USAGE: '/analytics/data-usage',
    CONNECTIONS: '/analytics/connections',
    SPENDING: '/analytics/spending',
    EXPORT: '/analytics/export',
  },

  // ==================== SETTINGS ====================
  SETTINGS: {
    PREFERENCES: '/settings/preferences',
    UPDATE_PREFERENCES: '/settings/preferences',
    PRIVACY: '/settings/privacy',
    UPDATE_PRIVACY: '/settings/privacy',
    SECURITY: '/settings/security',
    TWO_FA_ENABLE: '/settings/security/2fa/enable',
    TWO_FA_VERIFY: '/settings/security/2fa/verify',
    TWO_FA_DISABLE: '/settings/security/2fa/disable',
    REVOKE_SESSION: (id: string) => `/settings/security/sessions/${id}`,
    REVOKE_ALL_SESSIONS: '/settings/security/sessions/revoke-all',
  },

  // ==================== ORGANIZATION ====================
  ORG: {
    INVITATIONS: '/org/invitations',
    DETAIL: (id: string) => `/org/organizations/${id}`,
  },

  OPERATOR: {
    ORGANIZATION_REQUEST: '/operator/organization-request',
    COUNTRY_MANAGER_REQUEST: '/operator/country-manager-request',
    // KX-22 (model A) — ledger-based operator payouts.
    PAYOUTS: '/operator/payouts',
    PAYOUTS_BALANCE: '/operator/payouts/balance',
    PAYOUTS_BREAKDOWN: '/operator/payouts/breakdown',
    // KX-39 (T-32): KYC moved to the payout boundary.
    KYC_STATUS: '/operator/kyc/status',
    KYC_SUBMIT: '/operator/kyc/submit',
    // Purpose-built KYC upload — decoupled from registration (no registrationId).
    KYC_UPLOAD_DOCUMENT: '/operator/kyc/upload-document',
  },




  // ==================== STORE ====================
STORE: {
  PRODUCTS: '/operator/store/products',
  PRODUCT_BY_ID: (id: string) => `/operator/store/products/${id}`,
  CREATE_PRODUCT: '/operator/store/products',
  FILTERS: '/operator/store/filters',
  PURCHASE: '/operator/store/purchase',
},

 CART: {
  GET:    '/operator/cart',
  ADD:    '/operator/cart',           
  REMOVE: (id: string) => `/operator/cart/${id}`,  
  UPDATE: (id: string) => `/operator/cart/${id}`, 
  CLEAR:  '/operator/cart',          
},


 WISHLIST: {
  GET:    '/operator/wishlist',
  ADD:    '/operator/wishlist',
  REMOVE: (id: string) => `/operator/wishlist/${id}`,
  CLEAR:  '/operator/wishlist',
},

CHECKOUT: {
  GET:    '/operator/checkout',
  CREATE: '/operator/checkout',
  VERIFY: '/operator/checkout/verify',
},

  // ==================== ORDERS ====================
  ORDERS: {
  GET_ALL: '/operator/orders',
  GET_BY_ID: (id: string) => `/operator/orders/${id}`,
  CANCEL:    (id: string) => `/operator/orders/${id}/cancel`,
},
  // ==================== NETWORKS ====================
  NETWORKS: {
    SCAN: '/networks/scan',
    CONFIGURE: '/networks/configure',
    TEST: '/networks/test',
  },

  // ==================== SETUP ====================
  SETUP: {
    AUTHENTICATE: '/devices/authenticate',
    FINALIZE: '/devices/finalize',
    STATUS: (deviceId: string) => `/devices/${deviceId}/status`,
  },

  // ==================== ADMIN ====================
  ADMIN: {
    PLAN_CREATE: '/admin/plans',
    PLAN_EDIT: (planId: string) => `/admin/plans/${planId}`,
    PLAN_DELETE: (planId: string) => `/admin/plans/${planId}`,
    ANALYTICS: '/admin/analytics',
    // ---- Plans (admin CRUD) ----
    PLANS: '/admin/plans',
    TEAM: {
      ADMINS: '/admin/team/admins',
      SET_PASSWORD: (id) => `/admin/team/admins/${id}/password`,
      SET_ACTIVE: (id) => `/admin/team/admins/${id}/active`,
    },
    PLAN_DETAIL: (id: string) => `/admin/plans/${id}`,
    PLAN_UPDATE: (id: string) => `/admin/plans/${id}`,
    PLAN_TOGGLE: (id: string) => `/admin/plans/${id}/toggle`,
    // ---- Tax Profiles ----
    TAX_PROFILES: '/admin/tax-profiles',
    TAX_PROFILE_DETAIL: (id: string) => `/admin/tax-profiles/${id}`,
    TAX_PROFILE_TOGGLE: (id: string) => `/admin/tax-profiles/${id}/toggle`,
    // ---- Activation Fees (KX-33: BYO activation fee schedule) ----
    ACTIVATION_FEES: '/admin/activation-fees',
    ACTIVATION_FEE_DETAIL: (id: string) => `/admin/activation-fees/${id}`,
    // Country/currency matrix — source of truth for the country dropdown + currency
    COMMERCIAL_CURRENCIES: '/admin/commercial-config/currencies',
    USERS: '/admin/users',
    SETTINGS: '/admin/settings',
    // ---- Currency / country matrix (T-15) ----
    COMMERCIAL_CURRENCIES: '/admin/commercial-config/currencies',
    COMMERCIAL_CURRENCY: (id: string) => `/admin/commercial-config/currencies/${id}`,
    // ---- Rate cards (T-15 / KX-106) — versioned revenue split per country ----
    RATE_CARDS: '/admin/rate-cards',
    RATE_CARD: (id: string) => `/admin/rate-cards/${id}`,
    RATE_CARD_SUBMIT: (id: string) => `/admin/rate-cards/${id}/submit`,
    RATE_CARD_APPROVE: (id: string) => `/admin/rate-cards/${id}/approve`,
    RATE_CARD_REJECT: (id: string) => `/admin/rate-cards/${id}/reject`,
    // ---- Admin Store: Kits / Kit Tiers / Products / Orders (restored) ----
    KITS: '/admin/kits',
    KIT_DETAIL: (id) => `/admin/kits/${id}`,
    KIT_APPROVE: (id) => `/admin/kits/${id}/approve`,
    KIT_REJECT: (id) => `/admin/kits/${id}/reject`,
    KIT_TIERS: '/admin/kits/tiers',
    KIT_TIER_UPDATE: (key) => `/admin/kits/tiers/${key}`,
    PRODUCTS: '/admin/products',
    PRODUCT_CREATE: '/admin/products',
    PRODUCT_DETAIL: (id) => `/admin/products/${id}`,
    PRODUCT_UPDATE: (id) => `/admin/products/${id}`,
    PRODUCT_DELETE: (id) => `/admin/products/${id}`,
    PRODUCT_DUPLICATES: '/admin/products/duplicates',
    PRODUCT_UPLOAD_IMAGE: '/admin/products/upload-image',
    ORDERS: '/admin/orders',
    ORDER_DETAIL: (id) => `/admin/orders/${id}`,
    ORDER_STATUS: (id) => `/admin/orders/${id}/status`,
    ORDER_PAYMENT_STATUS: (id) => `/admin/orders/${id}/payment-status`,
    DASHBOARD: '/admin/dashboard',
    USER: '/admin/dashboard/recent-users',
    HOTSPOT: '/admin/dashboard/top-hotspots',
    GROWTH: '/admin/dashboard/user-growth',
    USER_STATISTICS: '/admin/users/stats',
    USER_USERS: '/admin/users',
    USER_EXPORT: '/admin/dashboard/recent-users/export',
    EDIT_USER_USERS: '/admin/users',
    SUSPEND_USER: (userId: string) => `/admin/users/${userId}/suspend`,
    UNSUSPEND_USER: (userId: string) => `/admin/users/${userId}/unsuspend`,
    WITHDRAWAL_ACCESS: (userId: string) => `/admin/users/${userId}/withdrawal-access`,
    HOTSPOT_STATISTICS: '/admin/hotspots/stats',
    HOTSPOT_GRAPH: '/admin/hotspots/usage-stats',
    HOTSPOTS_HOTSPOTS: '/admin/hotspots',
    REFERRAL_STATS: '/admin/referrals/dashboard',
    REFERRAL_HOTSPOT: '/admin/referrals/list',
    REFERRAL_GRAPH: '/admin/referrals/fraud-analysis',
    PAYOUT_USERS: '/admin/payouts/stats',
    PAYOUT_GRAPH: '/admin/payouts/trends',
    // KX-22 (model A) — fiat payout list + lifecycle (completing posts the ledger debit).
    FIAT_PAYOUTS: '/admin/payouts/list',
    FIAT_PAYOUT_STATUS: (id: string) => `/admin/payouts/${id}/status`,
    HOTSPOT_LOCATION: '/admin/hotspots/locations',
    ORGANIZATIONS: '/admin/organizations',
    ORGANIZATION_DETAIL: (organizationId: string) => `/admin/organizations/${organizationId}`,
    REJECT_ORGANIZATION: (organizationId: string) => `/admin/organizations/${organizationId}/reject`,
    ORGANIZATION_REQUESTS: '/admin/organizations/requests',
    REJECT_ORGANIZATION_REQUEST: (requestId: string) => `/admin/organizations/requests/${requestId}/reject`,
    COUNTRY_MANAGERS: '/admin/country-managers',
    REJECT_COUNTRY_MANAGER: (countryManagerId: string) => `/admin/country-managers/${countryManagerId}`,
    COUNTRY_MANAGER_REQUESTS: '/admin/country-managers/requests',
    APPROVE_COUNTRY_MANAGER_REQUEST: (requestId: string) => `/admin/country-managers/requests/${requestId}/approve`,
    REJECT_COUNTRY_MANAGER_REQUEST: (requestId: string) => `/admin/country-managers/${requestId}`,
    GRANT_DISTRIBUTION: '/admin/organizations/grant-distribution',
    TOKEN_DISTRIBUTION: '/admin/organizations/token-distribution',
    ORGANIZATION_DISTRIBUTION_REQUESTS: '/admin/organizations/distribution-requests',
    TRANSACTIONS: '/admin/transactions',
    DISTRIBUTIONS_SUMMARY: '/admin/distributions/summary',

    // ── Admin KXT endpoints ──
    KXT_STATS:            '/admin/kxt/stats',
    KXT_POOL:             '/admin/kxt/pool',
    KXT_LEADERBOARD:      '/admin/kxt/leaderboard',
    KXT_CONTRACT_STATUS:  '/admin/kxt/contract/status',
    KXT_CONTRACT_PAUSE:   '/admin/kxt/contract/pause',
    KXT_CONTRACT_UNPAUSE: '/admin/kxt/contract/unpause',
    KXT_RATE_USD:         '/admin/kxt/rate/usd',
    KXT_MINT:             '/admin/kxt/mint',
    KXT_WITHDRAWALS:      '/admin/kxt/withdrawals',
    KXT_WITHDRAWALS_APPROVED: '/admin/kxt/withdrawals/approved',
    KXT_WITHDRAWALS_REJECTED: '/admin/kxt/withdrawals/rejected',
    KXT_WITHDRAWAL_BY_ID: (id: string) => `/admin/kxt/withdrawals/${id}`,
    KXT_APPROVE:          (id: string) => `/admin/kxt/withdrawals/${id}/approve`,
    KXT_REJECT:           (id: string) => `/admin/kxt/withdrawals/${id}/reject`,
  },

  // ==================== REFERRALS ====================
  REFERRAL: {
    LINK: '/referrals/link',
    CODE: '/referrals/code',
    STATS: '/referrals/stats',
    ACTIVITY: '/referrals/activity',
    HISTORY: '/referrals/history',
    LEADERBOARD: '/referrals/leaderboard',
    PROGRESS: '/referrals/progress',
    REWARDS: '/referrals/rewards',
    USER_RANK: '/referrals/user-rank',
    SHARE: '/referrals/share',
    APPLY: '/referrals/apply',
  },

  // ==================== PROMOTIONS ====================
  PROMOTIONS: {
    ACTIVE: '/promotions/active',
    APPLY: '/promotions/apply',
    COUPONS: '/promotions/coupons',
  },

  // ==================== SUPPORT ====================

  ADMIN_SUPPORT: {
    STATS: '/admin/support/stats',
    TICKETS: '/admin/support/tickets',
    TICKET_DETAILS: (id: string) => `/admin/support/tickets/${id}`,
    REPLY: (id: string) => `/admin/support/tickets/${id}/messages`,
    STATUS: (id: string) => `/admin/support/tickets/${id}/status`,
  },
  SUPPORT: {
    TICKETS: '/support/tickets',
    TICKET_DETAILS: (id: string) => `/support/tickets/${id}`,
    REPLY: (id: string) => `/support/tickets/${id}/messages`,
    FAQ: '/support/faq',
    UPLOAD_ATTACHMENT: '/support/attachments',
    CLOSE: (id: string) => `/support/tickets/${id}/close`,
    RATE: (id: string) => `/support/tickets/${id}/rating`,
    SUBMIT: '/support',
  },

  // ==================== OPERATOR KIT ====================
  OPERATOR_KIT: {
    DASHBOARD: '/operatorkit/dashboard',
    RECENT_USERS: '/operatorkit/recentusers',
    // KX-36 node-cards (getNodeCards). Returns { success, nodes: NodeCard[] }.
    // Backend route is GET /api/operator/dashboard/nodes (routes/operator.js,
    // mounted at /api/operator, guarded by operatorAuth). VITE_API_URL already
    // includes the /api prefix, so the path here is relative to that.
    NODE_CARDS: '/operator/dashboard/nodes',
    ALL_KITS: '/operatorkit/allkits',
    KIT_DETAILS: (kitId: string) => `/operatorkit/kit/${kitId}`,
    ADD_KIT: '/operatorkit/addkit',
    UPDATE_KIT: (kitId: string) => `/operatorkit/kit/${kitId}`,
    DELETE_KIT: (kitId: string) => `/operatorkit/kit/${kitId}`,
    SEARCH_KITS: '/operatorkit/search',
  },

  // ==================== REGISTER KIT ====================
  REGISTER_KIT: {
    STEP1: '/registerkit/step1',
    STEP2: '/registerkit/step2',
    STEP3: '/registerkit/step3',
    UPLOAD_DOCUMENT: '/registerkit/upload-document',
    PROCEED_VERIFICATION: '/registerkit/proceed-verification',
    SUBMIT_APPROVAL: '/registerkit/submit-approval',
    STATUS: (registrationId: string) => `/registerkit/status/${registrationId}`,
    // KX-33 / T-26: live "we can see your router" probe for the BYO flow.
    CONNECTIVITY_CHECK: '/registerkit/connectivity-check',
  },

  // ==================== INDEPENDENT OPERATOR ====================
  INDEPENDENT_OPERATOR: {
    HOTSPOTS: '/independentoperator/hotspots',
    HOTSPOT_DETAILS: (hotspotId: string) => `/independentoperator/hotspot/${hotspotId}`,
    ADD_HOTSPOT: '/independentoperator/add-hotspot',
    UPDATE_HOTSPOT: (hotspotId: string) => `/independentoperator/hotspot/${hotspotId}`,
    DELETE_HOTSPOT: (hotspotId: string) => `/independentoperator/hotspot/${hotspotId}`,
  },

  // ==================== ALL KITS ====================
  ALL_KITS: {
    LIST: '/allkits/list',
    KIT_DETAILS: (kitId: string) => `/allkits/kit/${kitId}`,
    EXPORT: '/allkits/export',
    DONATE: '/allkits/donate',
    ADD_HOTSPOT: '/allkits/add-hotspot',
  },

  // ==================== SEARCH ====================
  SEARCH: {
    GLOBAL: '/search',
  },

  DONATE: {
    NGOS: '/app/donate/ngos',
    NGO_DONATE: '/app/donate/ngo',
    VERIFY_NGO_DONATION: '/app/donate/ngo/verify',
    TOP_DONATORS: '/app/donate/top-donators',
  },

  // ==================== HEALTH ====================
  HEALTH: '/health',
  SYSTEM_HEALTH: '/system-health',
};

// Helper function to build query strings
export const buildQueryString = (params: Record<string, unknown>): string => {
  const query = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');
  return query ? `?${query}` : '';
};

export default ENDPOINTS;
